import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { contactsApi } from '../../api/contacts'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { scopeWords } from '../../lib/orgs'
import { Avatar } from '../../components/Avatar'
import { Correct } from '../../components/Correct'
import { LineIcon } from '../../components/LineIcon'
import { VoteBadge, VoteButtons } from '../../components/VoteButtons'
import type { ContactView } from '../../types/contacts'
import { groupByLocation, isMachine, rankContacts, type ContactSort } from '../../lib/contacts'
import { InfluenceMap } from './InfluenceMap'
import { ReferralSources } from './ReferralSources'

// Contacts: everyone you know, read out of your own mail and ranked by how strong the tie is and how
// recently you touched it. The ones gone quiet or likely moved surface first as chips. Don's six-hour
// manual "top 25 / 50 / 100" pass, answered from the mail Scout already holds (PM-001, PM-002).
//
// The shape (REQ-125, owner 2026-10-01): the network map on top, the filter row beneath it, and the list
// grouped by where each person writes from (REQ-126) under that. A contact opened anywhere here — a dot on
// the map, a row, a referral source — goes to the card, a screen of its own; nothing opens in the page below.

const STAGE_WORDS: Record<string, string> = {
  lead: 'Lead', opportunity: 'Opportunity', proposal: 'Proposal out', negotiation: 'Negotiating', won: 'Won', lost: 'Lost', delivery: 'In delivery', dormant: 'Gone quiet',
}

type Filter = 'all' | 'strong' | 'dormant' | 'moved' | 'customer' | 'prospect' | 'vendor' | 'partner' | 'colleague' | 'competitor' | 'machine'
const ROLE_FILTERS: Filter[] = ['customer', 'prospect', 'vendor', 'partner', 'colleague', 'competitor']

export function Contacts() {
  const spoke = useSpoke()
  const navigate = useNavigate()
  const q = useQuery({ queryKey: ['contacts', spoke], queryFn: () => contactsApi.list(spoke), staleTime: 15_000 })
  const watch = useMutation({ mutationFn: () => contactsApi.watch(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  const photos = useMutation({ mutationFn: () => contactsApi.findPhotos(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  // THE RESEARCH QUEUE (PL-204; Don: "a queue where I select 10 names and then tell it what to do"): tick names on the list, say what to do,
  // and one job of yours briefs them in that order, web on, each landing in the console
  const [picked, setPicked] = useState<string[]>([])
  const [instruction, setInstruction] = useState('')
  const research = useMutation({ mutationFn: () => contactsApi.research(spoke, picked, instruction.trim()), onSuccess: (r) => { setPicked([]); navigate(`/jobs/${encodeURIComponent(r.job.id)}`) } })
  const togglePick = (id: string) => setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]))
  const researchDue = useMutation({ mutationFn: () => contactsApi.researchDue(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const [view, setView] = useState<'contacts' | 'referrals'>('contacts')
  // PL-156: lead with the ties BD is for (customers, prospects, partners and referral sources), then everyone
  // else, then colleagues and vendors; strength within a tier. The strength-only view is one toggle away.
  const [sort, setSort] = useState<ContactSort>('matters')
  // who sends you work (REQ-072): the ids alone, so a source's row carries the badge
  const referrals = useQuery({ queryKey: ['referral-sources', spoke], queryFn: () => contactsApi.referralSources(spoke), staleTime: 30_000 })
  const sources = useMemo(() => new Set((referrals.data?.sources ?? []).map((s) => s.personId)), [referrals.data])
  const openContact = (personId: string) => navigate(`/contacts/${encodeURIComponent(personId)}`)

  // PL-154: a machine or a shared mailbox (role 'machine') is not a contact; it is hidden unless asked for.
  // PL-213 (TFY-3 "Jeff is a colleague. Everyone at PPPLLC.com is a colleague"): a COLLEAGUE is not a contact either — your own
  // people are never on the list, never in the strong count, never in a role chip; the colleagues chip shows them when asked.
  const everything = q.data?.contacts ?? []
  const all = useMemo(() => everything.filter((c) => !isMachine(c) && c.person.role !== 'colleague'), [everything])
  const machines = useMemo(() => everything.filter(isMachine), [everything])
  const colleagues = useMemo(() => everything.filter((c) => c.person.role === 'colleague'), [everything])
  const scope = scopeWords(q.data?.scope)
  const counts = useMemo(() => {
    const c: Record<string, number> = {
      all: all.length,
      strong: all.filter((c) => (c.person.relationshipStrength ?? 0) >= 0.5).length,
      dormant: all.filter((c) => c.signals.some((s) => s.type === 'dormant')).length,
      moved: all.filter((c) => c.signals.some((s) => s.type === 'job-change')).length,
      machine: machines.length,
    }
    for (const r of ROLE_FILTERS) c[r] = all.filter((x) => x.person.role === r).length
    c.colleague = colleagues.length
    return c
  }, [all, machines, colleagues])

  const rows = useMemo(() => {
    let list = filter === 'machine' ? machines : filter === 'colleague' ? colleagues : all
    if (filter === 'strong') list = list.filter((c) => (c.person.relationshipStrength ?? 0) >= 0.5)
    else if (filter === 'dormant') list = list.filter((c) => c.signals.some((s) => s.type === 'dormant'))
    else if (filter === 'moved') list = list.filter((c) => c.signals.some((s) => s.type === 'job-change'))
    else if ((ROLE_FILTERS as string[]).includes(filter)) list = list.filter((c) => c.person.role === filter)
    const term = query.trim().toLowerCase()
    if (term) list = list.filter((c) =>
      c.person.name.toLowerCase().includes(term) ||
      c.companyName.toLowerCase().includes(term) ||
      (c.person.email ?? '').toLowerCase().includes(term) ||
      (c.person.location ?? '').toLowerCase().includes(term) ||
      (c.person.affiliations ?? []).some((a) => a.toLowerCase().includes(term)))
    return rankContacts(list, sort, sources)
  }, [all, machines, filter, query, sort, sources])
  const groups = useMemo(() => groupByLocation(rows), [rows])
  // 2026-10-07: the whole list at once was 70,000 DOM nodes and the slowest screen in the app. Everyone is in the list;
  // the rows are drawn as the page is scrolled (ROWS_FIRST at a time), and the filter box above narrows all of them
  const [drawn, setDrawn] = useState(ROWS_FIRST)
  useEffect(() => { setDrawn(ROWS_FIRST) }, [rows])
  const sentinel = useRef<HTMLTableRowElement>(null)
  useEffect(() => {
    const el = sentinel.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) setDrawn((n) => n + ROWS_FIRST) }, { rootMargin: '600px' })
    io.observe(el)
    return () => io.disconnect()
  }, [drawn, rows])
  // the groups, cut at the drawn count: a place past the cut is not drawn yet, a place across it is drawn up to it
  const visible = useMemo(() => {
    let left = drawn
    return groups.map((g) => { const take = Math.max(0, Math.min(g.rows.length, left)); left -= take; return { ...g, shown: g.rows.slice(0, take) } })
  }, [groups, drawn])
  const drawnCount = Math.min(drawn, rows.length)
  const placed = useMemo(() => all.filter((c) => c.person.location).length, [all])

  return (
    <section className="page contacts">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link>{scope ? ` · ${scope}` : ''}</p>
          <h1>Contacts</h1>
          <p className="lede">Everyone the pipeline read out of your mail — customers, prospects and the people who send you work first, then everyone else, then colleagues and vendors; within each, how strong the tie is and how recently you touched it. The ones gone quiet or likely moved are flagged. Notice feeds, news lists and systems are never contacts.</p>
        </div>
        <div className="actions">
          <Link className="button" to="/contacts?view=cards" title="One person at a time, strongest reason first: right for important, left for not now">Swipe through them</Link>
          <button className="button" onClick={() => watch.mutate()} disabled={watch.isPending} title="Re-weighs every relationship from your sent mail, flags dormant ties and likely moves, and reads where each contact writes from off their own signature.">{watch.isPending ? 'Starting…' : 'Refresh relationships'}</button>
          <button className="button icon" onClick={() => photos.mutate()} disabled={photos.isPending} title={`Looks every contact with an email up for a published profile picture (Gravatar first). ${all.filter((c) => c.person.photoUrl).length} of ${all.length} have one.`}><LineIcon name="photo" size={15} />{photos.isPending ? 'Starting…' : 'Find photos'}</button>
        </div>
      </header>
      {watch.isError && <p className="error">{watch.error instanceof RefusedError ? watch.error.refusal.message : watch.error.message}</p>}
      {photos.isError && <p className="error">{photos.error instanceof RefusedError ? photos.error.refusal.message : photos.error.message}</p>}

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal"><strong>Unavailable</strong><p>{q.error.message}</p></div>}
      {q.isSuccess && all.length === 0 && (
        <div className="card">
          <p className="muted">No contacts yet. Read your threads for leads and opportunities first; the people written to become your contacts, then "Refresh relationships" weighs each tie.</p>
        </div>
      )}

      {/* the network, on top: the firm at the centre, the accounts around it, the people around each; a dot opens the card */}
      {q.isSuccess && all.length > 0 && (
        <div className="contacts-map">
          <InfluenceMap contacts={rows} firmName="Your firm" onOpen={openContact} onOpenAccount={(id) => navigate(`/accounts/${encodeURIComponent(id)}`)} />
        </div>
      )}

      {/* the filter textbox, beneath the map: what is typed narrows the map and the list together */}
      {q.isSuccess && all.length > 0 && (
        <div className="contact-filter-bar">
          <input className="contact-search" type="search" placeholder="Filter by person, company, place or affiliation…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Filter contacts" />
          <span className="muted small">{rows.length === all.length ? `${all.length} contacts` : `${rows.length} of ${filter === 'machine' ? machines.length : all.length}`}</span>
          <div className="view-toggle" title="Who matters: customers, prospects and referral sources first, then everyone else, then colleagues and vendors. By strength: how often and how recently you wrote, whoever they are.">
            <button className={`chip-filter ${sort === 'matters' ? 'on' : ''}`} onClick={() => setSort('matters')}>Who matters</button>
            <button className={`chip-filter ${sort === 'strength' ? 'on' : ''}`} onClick={() => setSort('strength')}>By strength</button>
          </div>
        </div>
      )}
      {q.isSuccess && all.length > 0 && (
        <div className="contact-filters">
          {(['all', 'strong', 'dormant', 'moved'] as Filter[]).map((f) => (
            <button key={f} className={`chip-filter ${filter === f ? 'on' : ''}`} onClick={() => setFilter(f)}>
              <strong>{counts[f]}</strong> <span>{f === 'all' ? 'contacts' : f === 'strong' ? 'strong ties' : f === 'dormant' ? 'gone quiet' : 'may have moved'}</span>
            </button>
          ))}
          <span className="filter-sep" />
          {ROLE_FILTERS.filter((r) => counts[r] > 0).map((r) => (
            <button key={r} className={`chip-filter role-${r} ${filter === r ? 'on' : ''}`} onClick={() => setFilter(r)} title={r === 'colleague' ? 'Your own people, read out of the mail. Never contacts: not on the list, not in the counts; shown here only when asked.' : undefined}><strong>{counts[r]}</strong> <span>{r === 'colleague' ? 'colleagues' : r + 's'}</span></button>
          ))}
          {counts.machine > 0 && (
            <button className={`chip-filter role-machine ${filter === 'machine' ? 'on' : ''}`} onClick={() => setFilter(filter === 'machine' ? 'all' : 'machine')} title="Notice feeds, news lists, systems and support desks read out of your mail. Never contacts; shown here only so you can see what was withdrawn."><strong>{counts.machine}</strong> <span>machine mail</span></button>
          )}
          <div className="view-toggle push">
            <button className={`chip-filter ${view === 'contacts' ? 'on' : ''}`} onClick={() => setView('contacts')} title="Everyone, grouped by where they write from"><strong>{rows.length}</strong> <span>by place</span></button>
            <button className={`chip-filter ${view === 'referrals' ? 'on' : ''}`} onClick={() => setView('referrals')} title="Who sends you work, and what it was worth"><strong>{referrals.data?.totals.received ?? 0}</strong> <span>referrals</span></button>
          </div>
        </div>
      )}

      {q.isSuccess && view === 'referrals' && <ReferralSources spoke={spoke} onOpen={openContact} />}

      {/* the list, grouped by where each person writes from: the biggest places first, the unplaced last */}
      {q.isSuccess && all.length > 0 && view === 'contacts' && rows.length === 0 && (
        <p className="muted">Nobody matches that. Clear the filter or the search to see everyone.</p>
      )}
      {view === 'contacts' && rows.length > 0 && (
        <>
          <p className="muted small contacts-placed">
            {placed === 0
              ? 'Nobody is placed yet: "Refresh relationships" reads where each contact writes from off the signature block of their own mail, and only that.'
              : `${placed} of ${all.length} say where they write from, read off the signature block of their own mail; the rest are listed last.`}
          </p>
          <div className={`research-queue ${picked.length > 0 ? 'on' : ''}`} role="group" aria-label="Research the people you picked">
            <span className="research-queue-count"><strong>{picked.length}</strong> picked</span>
            <input className="research-queue-ask" value={instruction} onChange={(e) => setInstruction(e.target.value)} maxLength={500} placeholder="What to do with them — warm reconnect, find a reason to write, industry POV…" aria-label="What to do with the people picked" disabled={research.isPending} />
            <button type="button" className="button small primary" onClick={() => research.mutate()} disabled={picked.length === 0 || research.isPending} title="One job of yours writes a brief for each person picked, in your order, with the web on and the source on every finding; your instruction is answered first. It lands in the console.">
              {research.isPending ? 'Starting…' : `Research ${picked.length === 1 ? 'them' : `these ${picked.length}`}`}
            </button>
            {picked.length > 0 && <button type="button" className="link-button small" onClick={() => setPicked([])} disabled={research.isPending}>Clear</button>}
            {research.isError && <span className="error small">{research.error instanceof RefusedError ? research.error.refusal.message : research.error.message}</span>}
            {picked.length === 0 && <span className="muted small">Tick names below to queue research on them, or</span>}
            {picked.length === 0 && <button type="button" className="link-button small" onClick={() => researchDue.mutate()} disabled={researchDue.isPending} title="Whoever is due a brief tonight — never briefed, overtaken by newer mail, or past the refresh line — up to the night's budget, starting now.">{researchDue.isPending ? 'Starting…' : "research whoever is due tonight's sweep now"}</button>}
            {researchDue.isError && <span className="error small">{researchDue.error instanceof RefusedError ? researchDue.error.refusal.message : researchDue.error.message}</span>}
          </div>
          <table className="grid contacts-grid">
            <thead>
              <tr>
                <th className="pick-cell" aria-label="Pick for research"></th>
                <th>Person</th>
                <th>Company</th>
                <th>Relationship</th>
                <th>Last touch</th>
                <th>In a pursuit</th>
                <th>Flags</th>
              </tr>
            </thead>
            {visible.filter((g) => g.shown.length > 0).map((g) => (
              <tbody key={g.location} className="location-group">
                <tr className="group-row"><th colSpan={7} scope="rowgroup"><span className="group-name">{g.location}</span><span className="muted small">{g.rows.length}</span></th></tr>
                {g.shown.map((c) => <ContactRow key={c.person.personId} spoke={spoke} c={c} referral={sources.has(c.person.personId)} onOpen={() => openContact(c.person.personId)} picked={picked.includes(c.person.personId)} onPick={() => togglePick(c.person.personId)} />)}
              </tbody>
            ))}
            {drawnCount < rows.length && (
              <tbody><tr ref={sentinel} className="group-more"><td colSpan={7}><span className="muted small">{drawnCount} of {rows.length} drawn — scroll for the rest</span></td></tr></tbody>
            )}
          </table>
        </>
      )}
    </section>
  )
}

/** How many rows are drawn at a time as the page is scrolled: enough to fill a screen, few enough to stay quick. */
export const ROWS_FIRST = 60

function ContactRow({ spoke, c, referral, onOpen, picked = false, onPick }: { spoke: string; c: ContactView; referral?: boolean; onOpen: () => void; picked?: boolean; onPick?: () => void }) {
  const strength = c.person.relationshipStrength ?? 0
  const pct = Math.round(strength * 100)
  const band = strength >= 0.6 ? 'strong' : strength >= 0.3 ? 'warm' : 'faint'
  const dormant = c.signals.find((s) => s.type === 'dormant')
  const moved = c.signals.find((s) => s.type === 'job-change')
  return (
    <tr className={`contact-row ${band} ${picked ? 'picked' : ''}`} onClick={onOpen} style={{ cursor: 'pointer' }} title="Open the card: everything on file about them, and your call on each fact">
      <td className="pick-cell" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={picked} onChange={onPick} aria-label={`Pick ${c.person.name} for research`} title="Queue research on them" />
      </td>
      <td className="person-cell">
        <Avatar name={c.person.name} photoUrl={c.person.photoUrl} persona={c.person.persona} role={c.person.role} size={34} />
        <div>
          <strong><Link className="contact-name-link" to={`/contacts/${encodeURIComponent(c.person.personId)}`} onClick={(e) => e.stopPropagation()}>{c.person.name}</Link></strong>
          {referral && <span className="tag sig" title="Sent work your way: a referral source, read from your own mail">referral</span>}
          <VoteBadge vote={c.vote} />
          {c.person.nameBasis === 'address' && <span className="tag name-note" title="No name is on file for this address; this one is made from it">name inferred from the address</span>}
          {c.person.nameBasis === 'not-a-name' && <span className="tag name-note" title="What is on file is a date, a number or an address; the nightly tidy puts it right">not a name — being put right</span>}
          {c.person.nameBasis === 'partial' && <span className="tag name-note" title="A first name is all the mail gave; the nightly tidy completes it from a signature, a greeting or the address">first name only</span>}
          {c.person.goesBy && <span className="tag name-note" title="How you greet them in your own mail; every draft opens with it">goes by {c.person.goesBy}</span>}
          {c.person.title && <span className="muted small">{c.person.title}</span>}
          {c.person.email && <span className="muted small">{c.person.email}</span>}
        </div>
      </td>
      <td className="contact-company" onClick={(e) => e.stopPropagation()}>
        {c.companyName}{c.companyDomain && <span className="muted small">{c.companyDomain}</span>}
        <Correct kind="person-company" entityId={c.person.personId} entityLabel={`${c.person.name} · ${c.companyName}`} currentValue={c.person.companyId} compact />
        {c.person.companyId && <Correct kind="company-kind" entityId={c.person.companyId} entityLabel={c.companyName} compact />}
        {c.person.companyId && <Link className="small" to={`/workbench/${encodeURIComponent(c.person.companyId)}?person=${encodeURIComponent(c.person.personId)}`}>Workbench</Link>}
      </td>
      <td>
        <div className="strength-bar" title={`${pct}% — frequency and recency of your mail`}>
          <span className={`strength-fill ${band}`} style={{ width: `${Math.max(4, pct)}%` }} />
        </div>
        <span className="muted small">{pct}%</span>
      </td>
      <td>{c.lastActivity ? <>{formatDate(c.lastActivity)}<span className="muted small">{c.dormancyDays} days ago</span></> : <span className="muted">—</span>}</td>
      <td>{c.stage ? <span className={`tag stage ${c.stage}`}>{STAGE_WORDS[c.stage] ?? c.stage}</span> : <span className="muted">—</span>}</td>
      <td className="flag-cell" onClick={(e) => e.stopPropagation()}>
        {dormant && <><span className="tag sig watch" title={dormant.headline}>gone quiet</span><Correct kind="signal" entityId={dormant.signalId} entityLabel={`${c.person.name} · gone quiet`} currentValue="dormant" compact /></>}
        {moved && <><span className="tag sig watch" title={moved.detail ?? moved.headline}>may have moved</span><Correct kind="signal" entityId={moved.signalId} entityLabel={`${c.person.name} · may have moved`} currentValue="job-change" compact /></>}
        {!dormant && !moved && <span className="muted small">—</span>}
        <VoteButtons spoke={spoke} personId={c.person.personId} vote={c.vote} compact />
      </td>
    </tr>
  )
}
